import { useEffect, useRef } from 'react';

export interface CardDriftOptions {
  /** Amplitude (px) for the drift oscillation. */
  amp: number;
  /** Speed multiplier for the oscillation. */
  speed: number;
  /** Phase offset (radians) so each card drifts differently. */
  phase: number;
}

/**
 * Attaches a continuous gentle sine-wave drift to the element bound to the
 * returned ref, writing --x and --y CSS custom properties each frame.
 * Supports pointer-drag: pointerdown/move/up capture a manual offset and
 * add/remove the `dragging` class.
 *
 * Under `prefers-reduced-motion: reduce`, the rAF loop is skipped entirely
 * and --x/--y stay at 0.
 */
export function useCardDrift({ amp, speed, phase }: CardDriftOptions) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Manual drag offset persisted between frames
    let ox = 0;
    let oy = 0;
    let drag: { startX: number; startY: number; baseX: number; baseY: number } | null = null;
    let rafId: number | null = null;
    let t = 0;

    function frame() {
      t += 1;
      if (!drag) {
        const dx = Math.sin(t * speed * 0.06 + phase) * amp;
        const dy = Math.cos(t * speed * 0.06 + (phase + 1)) * amp;
        el!.style.setProperty('--x', `${(ox + dx).toFixed(2)}px`);
        el!.style.setProperty('--y', `${(oy + dy).toFixed(2)}px`);
      }
      rafId = requestAnimationFrame(frame);
    }

    if (!reduced) {
      rafId = requestAnimationFrame(frame);
    }

    // Drag handlers
    function onPointerDown(e: PointerEvent) {
      el!.setPointerCapture(e.pointerId);
      el!.classList.add('dragging');
      drag = { startX: e.clientX, startY: e.clientY, baseX: ox, baseY: oy };
    }

    function onPointerMove(e: PointerEvent) {
      if (!drag) return;
      ox = drag.baseX + (e.clientX - drag.startX);
      oy = drag.baseY + (e.clientY - drag.startY);
      el!.style.setProperty('--x', `${ox}px`);
      el!.style.setProperty('--y', `${oy}px`);
    }

    function onPointerUp() {
      if (!drag) return;
      drag = null;
      el!.classList.remove('dragging');
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [amp, speed, phase]);

  return ref;
}
